import { Check, ChevronsUpDown, Plus } from "lucide-react"
import React from "react"

import {
  Badge,
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@atlant/ui"

interface FormMultiSelectOption {
  label: string
  value: string
}

interface FormMultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  name?: string
  label?: string
  placeholder?: string
  options: FormMultiSelectOption[]
  disabled?: boolean
  className?: string
  allowCustom?: boolean
  error?: string
}

export const FormMultiSelect = ({
  value,
  onChange,
  name,
  label,
  placeholder = "Select options",
  options,
  disabled,
  className,
  allowCustom = false,
  error,
}: FormMultiSelectProps) => {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")

  const selectedValues = Array.isArray(value) ? value : []
  const selectedSet = new Set<string>(selectedValues)

  const optionValues = new Set(options.map((opt) => opt.value))
  const customValues = selectedValues.filter((val) => !optionValues.has(val))

  const handleSelect = (val: string) => {
    const next = new Set<string>(selectedSet)
    if (next.has(val)) {
      next.delete(val)
    } else {
      next.add(val)
    }
    onChange(Array.from(next))
  }

  const handleAddCustom = () => {
    const trimmed = searchValue.trim()
    if (trimmed && !selectedSet.has(trimmed)) {
      const next = new Set<string>(selectedSet)
      next.add(trimmed)
      onChange(Array.from(next))
      setSearchValue("")
    }
  }

  const handleClear = () => {
    onChange([])
  }

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchValue.toLowerCase()),
  )

  const showAddCustom =
    allowCustom &&
    searchValue.trim() &&
    !selectedSet.has(searchValue.trim()) &&
    !optionValues.has(searchValue.trim())

  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel
          htmlFor={id}
          className="text-muted-foreground pl-1 text-sm font-medium"
        >
          {label}
        </FieldLabel>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            type="button"
            className={cn(
              "shadow-custom! border-input hover:bg-input/10 w-full justify-between bg-white text-left font-normal dark:shadow-none!",
              selectedSet.size === 0 && "text-gray-400",
            )}
          >
            <div className="flex flex-1 items-center gap-2 overflow-hidden">
              {selectedSet.size === 0 ? (
                <span>{placeholder}</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {options
                    .filter((opt) => selectedSet.has(opt.value))
                    .map((option) => (
                      <Badge
                        key={option.value}
                        variant="secondary"
                        className="rounded-sm px-1 font-normal text-black dark:text-slate-200"
                      >
                        {option.label}
                      </Badge>
                    ))}
                  {customValues.map((v) => (
                    <Badge
                      key={v}
                      variant="outline"
                      className="rounded-sm px-1 font-normal text-black dark:text-slate-200"
                    >
                      {v}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--radix-popper-anchor-width) p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${label?.toLowerCase() || "options"}...`}
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>
                {allowCustom ? (
                  <div className="py-6 text-center text-sm">No results found.</div>
                ) : (
                  "No results found."
                )}
              </CommandEmpty>

              {filteredOptions.length > 0 && (
                <CommandGroup heading="Options">
                  {filteredOptions.map((option) => {
                    const isSelected = selectedSet.has(option.value)
                    return (
                      <CommandItem
                        key={option.value}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <div
                          className={cn(
                            "mr-2 flex size-4 items-center justify-center rounded-[4px] border",
                            isSelected
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-input [&_svg]:invisible",
                          )}
                        >
                          <Check className="text-primary-foreground size-3.5" />
                        </div>
                        <span>{option.label}</span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}

              {customValues.length > 0 && (
                <CommandGroup heading="Custom">
                  {customValues.map((v) => (
                    <CommandItem key={v} onSelect={() => handleSelect(v)}>
                      <div className="bg-primary border-primary text-primary-foreground mr-2 flex size-4 items-center justify-center rounded-[4px] border">
                        <Check className="text-primary-foreground size-3.5" />
                      </div>
                      <span>{v}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {showAddCustom && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem onSelect={handleAddCustom}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add "{searchValue}"
                    </CommandItem>
                  </CommandGroup>
                </>
              )}

              {selectedSet.size > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleClear}
                      className="justify-center text-center"
                    >
                      Clear selection
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
